import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Loader2, Sparkles, Volume2, Zap, Mic, Trash2, StickyNote } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { fetchTTS, playBase64, stopAudio } from "@/lib/tts";
import { toast } from "sonner";

const INTENTS = [
  { key: "work", val: "du travail sérieux et de la concentration", labelKey: "intent_work" },
  { key: "company", val: "de la compagnie : discuter, être écouté", labelKey: "intent_company" },
  { key: "advice", val: "des conseils et de l'aide pour décider", labelKey: "intent_advice" },
  { key: "relax", val: "se détendre et se changer les idées", labelKey: "intent_relax" },
  { key: "seduction", val: "de la séduction, du flirt et de l'intimité (chaud et sensuel, non explicite)", labelKey: "intent_seduction" },
];

const SPEECH_LANGS = { en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE", it: "it-IT", pt: "pt-PT", ru: "ru-RU", ar: "ar-SA", zh: "zh-CN", ja: "ja-JP" };

export default function CoachChat({ open, onClose }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const coachVoice = user?.profile?.gender === "homme" ? "onyx" : "nova";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [usage, setUsage] = useState(null);
  const [intent, setIntent] = useState("");
  const [category, setCategory] = useState("general");
  const [switching, setSwitching] = useState(false);
  const [listening, setListening] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  useEffect(() => () => { stopAudio(); try { recognitionRef.current?.stop(); } catch { /* ignore */ } }, []);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem("gaimtime_token");
      const res = await fetch(`${API}/coach/usage`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch { /* ignore */ }
  };

  const speak = async (idx, text) => {
    if (speakingIdx === idx) { stopAudio(); setSpeakingIdx(null); return; }
    if (!text) return;
    setSpeakingIdx(idx);
    try {
      const audio = await fetchTTS(text, coachVoice);
      playBase64(audio, () => setSpeakingIdx(null));
    } catch (e) {
      setSpeakingIdx(null);
      if (e?.response?.status === 429) toast.error(t("tts_limit_reached"));
    }
  };

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  // Reads an SSE stream and appends each chunk into the last (assistant) message.
  const streamInto = async (res) => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const raw = part.replace(/^data: ?/, "");
        if (!raw || raw === "[DONE]") continue;
        let chunk = raw;
        try { const o = JSON.parse(raw); if (o && typeof o.t === "string") chunk = o.t; } catch { /* plain text fallback */ }
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
        scrollBottom();
      }
    }
  };

  const loadHistory = async (cat) => {
    try {
      const token = localStorage.getItem("gaimtime_token");
      const res = await fetch(`${API}/coach/history?category=${encodeURIComponent(cat)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
      scrollBottom();
    } catch { /* ignore */ }
  };

  const loadNote = async (cat) => {
    try {
      const { data } = await api.get("/coach/note", { params: { category: cat } });
      setNote(data.note || "");
      return data.note || "";
    } catch { setNote(""); return ""; }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      let cat = "general";
      let curIntent = "";
      try {
        const r = await api.get("/coach/intent");
        curIntent = r.data.intent || "";
        const found = INTENTS.find((it) => it.val === curIntent);
        cat = found ? found.key : "general";
      } catch { /* ignore */ }
      setIntent(curIntent);
      setCategory(cat);
      await loadHistory(cat);
      await loadNote(cat);
      fetchUsage();
    })();
  }, [open]);

  const chooseIntent = async (val, key) => {
    if (busy || switching) return;
    const active = intent === val;
    const nextVal = active ? "" : val;
    const nextCat = active ? "general" : key;
    setIntent(nextVal);
    setCategory(nextCat);
    setSwitching(true);
    try {
      await api.post("/coach/intent", { intent: nextVal });
      await loadHistory(nextCat);
      const n = await loadNote(nextCat);
      if (nextVal) { setNoteText(n); setNoteOpen(true); }
    } catch { /* ignore */ }
    finally { setSwitching(false); }
  };

  const toggleMic = () => {
    if (!SR) { toast.error(t("mic_unsupported")); return; }
    if (listening) { try { recognitionRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    try {
      const rec = new SR();
      rec.lang = SPEECH_LANGS[lang] || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (e) => {
        let txt = "";
        for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
        setInput(txt);
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recognitionRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); toast.error(t("mic_unsupported")); }
  };

  const openNote = () => { setNoteText(note); setNoteOpen(true); };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      await api.put("/coach/note", { category, note: noteText });
      setNote(noteText);
      toast.success(t("note_saved"));
      setNoteOpen(false);
    } catch { toast.error(t("err_generic")); }
    finally { setNoteSaving(false); }
  };

  const clearThread = async () => {
    setClearing(true);
    try {
      await api.delete("/coach/history", { params: { category } });
      setMessages([]);
      setClearOpen(false);
      toast.success(t("thread_cleared"));
    } catch { toast.error(t("err_generic")); }
    finally { setClearing(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || limitReached) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    scrollBottom();

    try {
      const token = localStorage.getItem("gaimtime_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, lang, category }),
      });
      await streamInto(res);
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Désolé, une erreur est survenue." };
        return copy;
      });
    } finally {
      setBusy(false);
      scrollBottom();
      fetchUsage();
    }
  };

  const limitReached = usage ? usage.can_send === false : false;

  const buyPack = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { type: "pack30", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error(t("err_generic")); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="text-white flex items-center gap-2 font-display">
            <Sparkles className="text-[#00E676]" size={18} /> {t("coach_title")}
          </SheetTitle>
        </SheetHeader>

        <div className="px-4 py-2.5 border-b border-white/10 shrink-0">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1.5">{t("intent_label")}</div>
          <div className="flex flex-wrap gap-1.5" data-testid="coach-intent-selector">
            {INTENTS.map((it) => {
              const active = intent === it.val;
              return (
                <button key={it.key} type="button" data-testid={`intent-${it.key}`}
                  onClick={() => chooseIntent(it.val, it.key)} disabled={busy || switching}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all disabled:opacity-50 ${
                    active ? "bg-[#00E676]/15 border-[#00E676]/50 text-[#00E676]" : "bg-[#111111] border-white/10 text-white/50 hover:border-white/25"
                  }`}>{t(it.labelKey)}</button>
              );
            })}
          </div>
        </div>

        <div className="px-4 py-1.5 border-b border-white/10 shrink-0 bg-[#0A0A0A]/60 flex justify-end" data-testid="coach-context-banner">
          <button type="button" data-testid="coach-clear-btn" onClick={() => setClearOpen(true)} title={t("clear_thread")}
            className="p-1.5 rounded-full text-white/50 hover:text-[#FF3B30] hover:bg-white/5 transition-colors">
            <Trash2 size={15} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="text-center text-white/40 text-sm mt-10 px-6">
              {t("coach_empty")}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${m.role === "user" ? "" : "flex items-end gap-1.5"}`}>
                <div className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#007BFF] rounded-[14px] rounded-tr-sm"
                    : "bg-[#1A1A1A] rounded-[14px] rounded-tl-sm text-white/90"
                }`}>
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 className="animate-spin" size={16} /> : "")}
                </div>
                {m.role === "assistant" && m.content && (
                  <button
                    data-testid={`coach-speak-btn-${i}`}
                    onClick={() => speak(i, m.content)}
                    title={t("listen_msg")}
                    className={`shrink-0 p-1.5 rounded-full transition-colors ${speakingIdx === i ? "text-[#00E676] bg-[#00E676]/10" : "text-white/40 hover:text-white"}`}
                  >
                    {speakingIdx === i ? <Loader2 className="animate-spin" size={15} /> : <Volume2 size={15} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10">
          {limitReached ? (
            <div className="text-center space-y-3 py-2" data-testid="coach-limit-offer">
              <div className="font-semibold text-white">{t("coach_limit_title")}</div>
              <div className="text-sm text-white/50">{t("coach_limit_desc")}</div>
              <Button data-testid="coach-buy-pack-btn" onClick={buyPack}
                className="w-full bg-[#00E676] hover:bg-[#00c765] text-black rounded-[12px] h-11 font-bold">
                <Zap size={16} className="mr-1.5" /> {t("coach_buy_pack")}
              </Button>
            </div>
          ) : (
            <>
              {usage && (
                <div className="text-xs text-center mb-2 text-white/40" data-testid="coach-usage">
                  {user?.is_admin ? (
                    <span className="text-[#00E676]">∞ Illimité (admin)</span>
                  ) : (
                    <>
                      {usage.remaining} {t("coach_daily_left")}
                      {usage.bonus > 0 && <span className="text-[#00E676]"> · +{usage.bonus} {t("coach_bonus_label")}</span>}
                    </>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  data-testid="coach-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("coach_ph")}
                  className="bg-[#111111] border-white/10 rounded-[12px] text-white placeholder:text-white/30 focus-visible:ring-[#00E676]"
                />
                <Button data-testid="coach-mic-btn" onClick={toggleMic} disabled={busy} title={listening ? t("mic_listening") : t("mic_hint")}
                  className={`rounded-[12px] px-4 transition-all ${listening ? "bg-red-500 hover:bg-red-600 text-white animate-pulse" : "bg-[#1A1A1A] hover:bg-[#222] text-white/70 border border-white/10"}`}>
                  <Mic size={18} />
                </Button>
                <Button data-testid="coach-send-btn" onClick={send} disabled={busy}
                  className="bg-[#00E676] hover:bg-[#00c765] text-black rounded-[12px] px-4">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                </Button>
              </div>
            </>
          )}
        </div>

        <Dialog open={noteOpen} onOpenChange={(o) => { setNoteOpen(o); if (!o) setNoteText(note); }}>
          <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md" data-testid="coach-note-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2">
                <StickyNote className="text-[#00E676]" size={20} /> {t("note_title")}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-white/60 -mt-1" data-testid="coach-note-desc">{t(`ctx_desc_${category}`)}</p>
            <Textarea data-testid="coach-note-textarea" value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder={t("note_ph")} maxLength={1000}
              className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] min-h-[140px] focus-visible:ring-[#00E676]" />
            <p className="text-xs text-white/40">{t("note_hint")}</p>
            <Button data-testid="coach-note-save-btn" onClick={saveNote} disabled={noteSaving}
              className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-11 font-semibold">
              {noteSaving ? <Loader2 className="animate-spin" /> : t("save")}
            </Button>
          </DialogContent>
        </Dialog>

        <Dialog open={clearOpen} onOpenChange={setClearOpen}>
          <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-sm" data-testid="coach-clear-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg font-bold">{t("clear_thread_q")}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-white/50">{t("clear_thread_desc")}</p>
            <div className="flex gap-3 mt-2">
              <Button data-testid="coach-clear-cancel" variant="outline" onClick={() => setClearOpen(false)}
                className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-[12px]">{t("cancel")}</Button>
              <Button data-testid="coach-clear-confirm" onClick={clearThread} disabled={clearing}
                className="flex-1 bg-[#FF3B30] hover:bg-[#d92c22] text-white rounded-[12px] font-semibold">
                {clearing ? <Loader2 className="animate-spin" /> : t("clear_confirm")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
